import React, { Suspense, useRef } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Stars, Html } from "@react-three/drei";
import * as THREE from "three";

// Singapore approx lat 1.35, lon 103.82
function latLonToVec3(lat, lon, r = 2) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

const EARTH_TEXTURE_URL =
  "https://cdn.jsdelivr.net/gh/mrdoob/three.js@r158/examples/textures/planets/earth_atmos_2048.jpg";
const EARTH_BUMP_URL =
  "https://cdn.jsdelivr.net/gh/mrdoob/three.js@r158/examples/textures/planets/earth_normal_2048.jpg";

function Earth() {
  const groupRef = useRef();
  const glowRef = useRef();
  const beaconRef = useRef();
  const haloRef = useRef();

  const [colorMap, normalMap] = useLoader(THREE.TextureLoader, [
    EARTH_TEXTURE_URL,
    EARTH_BUMP_URL,
  ]);

  useFrame((state, dt) => {
    if (groupRef.current) groupRef.current.rotation.y += dt * 0.08;
    const t = state.clock.getElapsedTime();
    if (beaconRef.current) {
      const s = 1 + Math.sin(t * 3) * 0.25;
      beaconRef.current.scale.set(s, s, s);
    }
    if (haloRef.current) {
      const s = 1 + Math.sin(t * 3) * 0.6;
      haloRef.current.scale.set(s, s, s);
      haloRef.current.material.opacity = 0.35 - Math.sin(t * 3) * 0.15;
    }
  });

  const sg = latLonToVec3(1.35, 103.82, 2.02);
  const sgLabel = latLonToVec3(1.35, 103.82, 2.55);

  return (
    <group ref={groupRef}>
      {/* Earth sphere with texture - use basic material so the whole globe stays visible */}
      <mesh>
        <sphereGeometry args={[2, 96, 96]} />
        <meshPhongMaterial
          map={colorMap}
          normalMap={normalMap}
          bumpScale={0.05}
          shininess={12}
          emissive={new THREE.Color("#2a3468")}
          emissiveIntensity={0.55}
        />
      </mesh>

      {/* Cyan wireframe overlay for futuristic vibe (subtle) */}
      <mesh>
        <sphereGeometry args={[2.015, 40, 40]} />
        <meshBasicMaterial color="#00F0FF" wireframe transparent opacity={0.045} />
      </mesh>

      {/* Atmospheric glow */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[2.22, 48, 48]} />
        <meshBasicMaterial color="#00F0FF" transparent opacity={0.10} side={THREE.BackSide} />
      </mesh>

      {/* Singapore HQ beacon (pulsing) */}
      <mesh position={sg} ref={beaconRef}>
        <sphereGeometry args={[0.06, 24, 24]} />
        <meshStandardMaterial color="#FF007F" emissive="#FF007F" emissiveIntensity={4} />
      </mesh>
      {/* Halo ring around beacon */}
      <mesh position={sg} ref={haloRef}>
        <sphereGeometry args={[0.16, 24, 24]} />
        <meshBasicMaterial color="#FF007F" transparent opacity={0.3} />
      </mesh>

      {/* Vertical marker beam */}
      <mesh position={sg.clone().multiplyScalar(1.08)}>
        <cylinderGeometry args={[0.015, 0.015, 0.35, 8]} />
        <meshBasicMaterial color="#FF007F" transparent opacity={0.85} />
      </mesh>

      {/* Floating Singapore HQ label */}
      <Html
        position={sgLabel}
        center
        distanceFactor={6}
        occlude={false}
        zIndexRange={[100, 0]}
      >
        <div
          data-testid="globe-singapore-label"
          className="pointer-events-none select-none whitespace-nowrap"
          style={{
            padding: "4px 10px",
            borderRadius: "999px",
            background: "rgba(255, 0, 127, 0.15)",
            border: "1px solid rgba(255, 0, 127, 0.7)",
            color: "#fff",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "10px",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            boxShadow: "0 0 18px rgba(255, 0, 127, 0.55)",
            backdropFilter: "blur(6px)",
          }}
        >
          ★ Singapore · HQ
        </div>
      </Html>
    </group>
  );
}

function Fallback() {
  return (
    <mesh>
      <sphereGeometry args={[2, 32, 32]} />
      <meshStandardMaterial color="#0A0F24" wireframe />
    </mesh>
  );
}

export default function GlobeSection() {
  return (
    <section id="resources" className="relative py-28 px-6" data-testid="globe-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
        <div className="relative h-[480px] rounded-3xl overflow-hidden glass holo-border" data-testid="globe-canvas-wrapper">
          <Canvas camera={{ position: [0, 0, 5.5], fov: 50 }} dpr={[1, 1.5]}>
            <color attach="background" args={["#050816"]} />
            <ambientLight intensity={1.2} />
            <directionalLight position={[5, 3, 5]} intensity={1.8} color="#ffffff" />
            <directionalLight position={[-6, -2, -3]} intensity={0.6} color="#B200FF" />
            <pointLight position={[-5, -3, -5]} intensity={0.8} color="#B200FF" />
            <pointLight position={[5, 3, 5]} intensity={0.5} color="#00F0FF" />
            <Suspense fallback={<Fallback />}>
              <Stars radius={20} depth={40} count={800} factor={2} fade speed={1} />
              <Earth />
            </Suspense>
          </Canvas>

          {/* Legend overlay */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] tracking-widest text-white/70 uppercase pointer-events-none">
            <span className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#FF007F] shadow-[0_0_10px_#FF007F]" />
              Singapore · HQ
            </span>
            <span className="text-white/40">◆ MUNNEXA · Global Grid</span>
          </div>
        </div>

        <div>
          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">◆ GLOBAL PRESENCE · SINGAPORE HQ</p>
          <h2 className="text-4xl sm:text-5xl font-[Outfit] font-bold">From <span className="text-gradient">Singapore</span> to the world.</h2>
          <p className="mt-4 text-white/65 max-w-xl">A Singapore-born AI growth lab. We help SMEs win the next era with strategy, automation, and government-grade execution.</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              ["SME Solutions", "Tailored AI stack for 5–500 employee companies."],
              ["Singapore Growth", "Localized strategies, language, payments, channels."],
              ["Govt Grants Ready", "PSG / EDG-aligned roadmaps (subject to approval)."],
              ["Global Ready", "Deploy across SEA, MENA, EU markets."],
            ].map(([t, d], i) => (
              <div key={t} className="glass rounded-xl p-4" data-testid={`globe-feature-${i}`}>
                <div className="font-[Outfit] font-semibold">{t}</div>
                <div className="text-xs text-white/60 mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
